import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { Modal, Form, Input, Button, Alert, Typography, Space, Divider, Flex } from "antd"
import { MailOutlined, LockOutlined, PhoneOutlined, NumberOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { isValidPhoneNumber } from "libphonenumber-js"
import Spinner from "components/common/Spinner"
import {
  verifyGuestEmail,
  getGuestVerificationMethod,
  sendGuestBookingOtp,
  verifyGuestBookingOtp
} from "store/guest-access"
import { routes } from "router"
import styles from "./index.module.css"

const { Title, Text } = Typography

/**
 * Shown to logged-out users who arrive at a booking details page via an email/SMS link.
 * Confirms identity before granting access to the booking:
 * - Clients with a real email on file verify by entering that email.
 * - Phone-only clients (no real email, e.g. imported without one) verify via SMS OTP.
 */
const GuestEmailVerifyModal = ({ bookingUuid }) => {
  const { t } = useTranslation()
  const [emailForm] = Form.useForm()
  const [phoneForm] = Form.useForm()
  const [otpForm] = Form.useForm()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading, error, method, isMethodLoading, isOtpSent, phoneNumber } = useSelector((state) => state.guestAccess)

  useEffect(() => {
    dispatch(getGuestVerificationMethod(bookingUuid))
  }, [bookingUuid]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleEmailSubmit = async (values) => {
    try {
      await dispatch(verifyGuestEmail(bookingUuid, values.email))
      // Success: Redux state updates authorized=true, component unmounts via parent re-render
    } catch {
      // Error is stored in Redux state and displayed below
    }
  }

  const handlePhoneSubmit = async (values) => {
    try {
      await dispatch(sendGuestBookingOtp(bookingUuid, values.phoneNumber))
      // Success: Redux state updates isOtpSent=true and stores phoneNumber for step 2
    } catch {
      // Error is stored in Redux state and displayed below
    }
  }

  const handleOtpSubmit = async (values) => {
    try {
      await dispatch(verifyGuestBookingOtp(bookingUuid, values.otp))
      // Success: Redux state updates authorized=true, component unmounts via parent re-render
    } catch {
      // Error is stored in Redux state and displayed below
    }
  }

  const renderEmailStep = () => (
    <Form form={emailForm} onFinish={handleEmailSubmit} layout="vertical" requiredMark={false}>
      <Form.Item
        name="email"
        label="Email Address"
        rules={[
          { required: true, message: "Please enter your email address" },
          { type: "email", message: "Please enter a valid email address" }
        ]}
      >
        <Input
          prefix={<MailOutlined className={styles.inputIcon} />}
          placeholder="your@email.com"
          size="large"
          autoComplete="email"
          autoFocus
        />
      </Form.Item>

      <Form.Item className={styles.submitFormItem}>
        <Button type="primary" htmlType="submit" size="large" block loading={loading}>
          Access Trip Details
        </Button>
      </Form.Item>
    </Form>
  )

  const renderPhoneStep = () => (
    <Form form={phoneForm} onFinish={handlePhoneSubmit} layout="vertical" requiredMark={false}>
      <Form.Item
        name="phoneNumber"
        label={t("guest_verify.phone_number")}
        rules={[
          { required: true, message: t("guest_verify.phone_number_required") },
          {
            validator: (_, value) => {
              if (!value || isValidPhoneNumber(value, "US")) return Promise.resolve()
              return Promise.reject(t("guest_verify.phone_number_invalid"))
            }
          }
        ]}
      >
        <Input
          prefix={<PhoneOutlined className={styles.inputIcon} />}
          placeholder="(555) 555-5555"
          size="large"
          autoComplete="tel"
          autoFocus
        />
      </Form.Item>

      <Form.Item className={styles.submitFormItem}>
        <Button type="primary" htmlType="submit" size="large" block loading={loading}>
          {t("guest_verify.send_verification_code")}
        </Button>
      </Form.Item>
    </Form>
  )

  const renderOtpStep = () => (
    <Form form={otpForm} onFinish={handleOtpSubmit} layout="vertical" requiredMark={false}>
      <Form.Item
        name="otp"
        label={t("guest_verify.verification_code")}
        rules={[{ required: true, message: t("guest_verify.verification_code_required") }]}
      >
        <Input
          prefix={<NumberOutlined className={styles.inputIcon} />}
          placeholder="12345"
          size="large"
          autoComplete="one-time-code"
          autoFocus
        />
      </Form.Item>

      <Form.Item className={styles.submitFormItem}>
        <Button type="primary" htmlType="submit" size="large" block loading={loading}>
          Access Trip Details
        </Button>
      </Form.Item>
    </Form>
  )

  const isPhoneFlow = method === "phone"

  let description = "Enter the email address associated with this booking to view your trip details."
  if (isPhoneFlow) {
    description = isOtpSent
      ? t("guest_verify.otp_description", { phoneNumber })
      : t("guest_verify.phone_description")
  }

  let stepContent = renderEmailStep()
  if (isPhoneFlow) {
    stepContent = isOtpSent ? renderOtpStep() : renderPhoneStep()
  }

  return (
    <Flex align="center" justify="center" style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      <Modal
        open={true}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        width={440}
        styles={{ mask: { background: "transparent" } }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div style={{ textAlign: "center" }}>
            <Flex align="center" justify="center" style={{ width: 56, height: 56, borderRadius: "50%", background: "#e6f4ff", margin: "0 auto 16px" }}>
              <LockOutlined style={{ fontSize: 24, color: "#1677ff" }} />
            </Flex>
            <Title level={4} style={{ marginBottom: 4 }}>
              Verify Your Identity
            </Title>
            {!isMethodLoading && <Text type="secondary">{description}</Text>}
          </div>

          {isMethodLoading ? (
            <Spinner />
          ) : (
            <>
              {error && (
                <Alert
                  message={error}
                  type="error"
                  showIcon
                  icon={isPhoneFlow ? <PhoneOutlined /> : <MailOutlined />}
                />
              )}

              {stepContent}

              <Divider plain style={{ margin: "0" }}>
                <Text type="secondary" style={{ fontSize: 13 }}>or</Text>
              </Divider>

              <Button size="large" block onClick={() => navigate(routes.signInV2())}>
                Sign In
              </Button>
            </>
          )}
        </Space>
      </Modal>
    </Flex>
  )
}

GuestEmailVerifyModal.propTypes = {
  bookingUuid: PropTypes.string.isRequired
}

export default GuestEmailVerifyModal
